import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertCircle, DollarSign, FileText, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SectionStatus {
  id: string;
  label: string;
  icon: React.ReactNode;
  isValid: boolean;
  fieldCount: number;
  validFieldCount: number;
}

interface SectionProgressIndicatorProps {
  sections: SectionStatus[];
  className?: string;
  compact?: boolean;
}

export const SectionProgressIndicator: React.FC<SectionProgressIndicatorProps> = ({
  sections,
  className,
  compact = false,
}) => {
  const allValid = sections.every(s => s.isValid);
  const totalFields = sections.reduce((acc, s) => acc + s.fieldCount, 0);
  const validFields = sections.reduce((acc, s) => acc + s.validFieldCount, 0);
  const progress = totalFields > 0 ? (validFields / totalFields) * 100 : 0;

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {sections.map((section, index) => (
          <React.Fragment key={section.id}>
            <motion.div
              initial={false}
              animate={{
                scale: section.isValid ? [1, 1.2, 1] : 1,
              }}
              transition={{ duration: 0.3 }}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full transition-colors duration-300",
                section.isValid 
                  ? "bg-green-500/20 text-green-500" 
                  : "bg-muted text-muted-foreground"
              )}
            >
              <AnimatePresence mode="wait">
                {section.isValid ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: 180 }}
                    transition={{ duration: 0.3, type: "spring" }}
                  >
                    <Check className="w-4 h-4" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="icon"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {section.icon}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            {index < sections.length - 1 && (
              <div 
                className={cn(
                  "h-0.5 w-4 transition-colors duration-300",
                  section.isValid ? "bg-green-500/50" : "bg-muted"
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {/* Progress Bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Form Progress</span>
          <span className={cn(
            "font-medium transition-colors",
            allValid ? "text-green-500" : "text-foreground"
          )}>
            {validFields}/{totalFields} fields valid
          </span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className={cn(
              "h-full rounded-full transition-colors duration-300",
              allValid 
                ? "bg-gradient-to-r from-green-500 to-emerald-400" 
                : "bg-gradient-to-r from-primary to-purple-500"
            )}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Section Status */}
      <div className="flex items-center justify-between gap-2">
        {sections.map((section, index) => (
          <React.Fragment key={section.id}>
            <div className="flex-1">
              <motion.div
                initial={false}
                animate={{
                  scale: section.isValid ? [1, 1.05, 1] : 1,
                }}
                transition={{ duration: 0.3 }}
                className={cn(
                  "flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all duration-300",
                  section.isValid 
                    ? "bg-green-500/10" 
                    : "bg-muted/50"
                )}
              >
                <div className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full transition-colors duration-300",
                  section.isValid 
                    ? "bg-green-500/20 text-green-500" 
                    : "bg-muted text-muted-foreground"
                )}>
                  <AnimatePresence mode="wait">
                    {section.isValid ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0, rotate: 180 }}
                        transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
                      >
                        <Check className="w-4 h-4" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="icon"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="w-4 h-4"
                      >
                        {section.icon}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <span className={cn(
                  "text-xs font-medium text-center transition-colors",
                  section.isValid ? "text-green-500" : "text-muted-foreground"
                )}>
                  {section.label}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {section.validFieldCount}/{section.fieldCount}
                </span>
              </motion.div>
            </div>
            {index < sections.length - 1 && (
              <motion.div 
                className="h-0.5 w-4 rounded-full"
                animate={{
                  backgroundColor: section.isValid ? 'rgb(34 197 94 / 0.5)' : 'rgb(148 163 184 / 0.3)'
                }}
                transition={{ duration: 0.3 }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Helper function to create section statuses for Reverse PITI calculator
export const createReversePitiSections = (validation: {
  desiredPiti: number;
  interestRate: number;
  loanTerm: number;
  downPaymentPercent: number;
  propertyTaxRate: number;
  annualInsurance: number;
  hoaFees: number;
  pmiRate: number;
  includePmi: boolean;
}): SectionStatus[] => {
  // Desired PITI section
  const desiredPitiValid = validation.desiredPiti >= 100 && validation.desiredPiti <= 50000;

  // Loan Details section
  const interestValid = validation.interestRate >= 0.1 && validation.interestRate <= 25;
  const termValid = [10, 15, 20, 25, 30].includes(validation.loanTerm);
  const downPaymentValid = validation.downPaymentPercent >= 0 && validation.downPaymentPercent <= 100;
  const loanDetailsValidCount = [interestValid, termValid, downPaymentValid].filter(Boolean).length;

  // Additional Costs section
  const taxValid = validation.propertyTaxRate >= 0 && validation.propertyTaxRate <= 10;
  const insuranceValid = validation.annualInsurance >= 0 && validation.annualInsurance <= 100000;
  const hoaValid = validation.hoaFees >= 0 && validation.hoaFees <= 5000;
  const pmiValid = !validation.includePmi || (validation.pmiRate >= 0 && validation.pmiRate <= 5);
  const costsValidCount = [taxValid, insuranceValid, hoaValid, pmiValid].filter(Boolean).length;

  return [
    {
      id: 'desired-piti',
      label: 'Target',
      icon: <DollarSign className="w-4 h-4" />,
      isValid: desiredPitiValid,
      fieldCount: 1,
      validFieldCount: desiredPitiValid ? 1 : 0,
    },
    {
      id: 'loan-details',
      label: 'Loan',
      icon: <FileText className="w-4 h-4" />,
      isValid: loanDetailsValidCount === 3,
      fieldCount: 3,
      validFieldCount: loanDetailsValidCount,
    },
    {
      id: 'additional-costs',
      label: 'Costs',
      icon: <Settings className="w-4 h-4" />,
      isValid: costsValidCount === 4,
      fieldCount: 4,
      validFieldCount: costsValidCount,
    },
  ];
};
